import React, { useContext, useEffect } from 'react';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import Bugsnag from '@bugsnag/js';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { makeStyles } from '@material-ui/core/styles';

// react router
import { Navigate, Routes, Route } from 'react-router-dom';

// components
import Login from 'routes/Login';
import Signup from 'routes/Signup';
import ForgotPassword from 'routes/ForgotPassword';
import WaitingRoom from 'routes/WaitingRoom';
import EmailVerification from 'routes/EmailVerification';
import SelectChapter from 'routes/SelectChapter';
import ReferralForm from 'routes/ReferralForm';
import ApplicationLayout from 'layout/ApplicationLayout';
import LoadingScreen from './components/LoadingScreen';
import NavigationHandler from 'components/NavigationHandler';

// helpers
import { SiteVisualDataContext } from 'components/SiteVisualData';
import OpenIDConnect from 'routes/Oidc';

// action creators
import { createSession } from 'store/actions/sessionActions';

// selectors
import { isSubmitting } from 'store/selectors/formSelectors';

type Props = {
  currentGroup: Group;
  currentGroupLoading: boolean;
  currentUser: CurrentUser;
  currentUserLoading: boolean;
  isSystemAdmin: boolean;
  sessionIsValid: boolean | 'unknown';
  userHasGroup: boolean;
  verifySession: (...args: any[]) => any;
};

const FORM_NAME = 'ssoLoginForm';

const App = ({
  currentGroup,
  currentGroupLoading,
  currentUser,
  currentUserLoading,
  isSystemAdmin,
  sessionIsValid,
  userHasGroup,
  verifySession,
}: Props) => {
  const classes = useStyles();
  const { pageTitle = '' } = useContext(SiteVisualDataContext);
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  const isSsoSubmitting = useSelector(isSubmitting(FORM_NAME));

  useEffect(() => {
    if (sessionIsValid === 'unknown') {
      const urlParams = new URLSearchParams(search);
      const code = urlParams.get('code');

      if (pathname === '/' && code) {
        dispatch(createSession({ code, formName: FORM_NAME }));

        // Clean the URL by removing the code parameter
        urlParams.delete('code');
        navigate(pathname, { replace: true });
      } else if (!isSsoSubmitting) {
        verifySession();
      }
    }
  }, [
    sessionIsValid,
    verifySession,
    isSsoSubmitting,
    search,
    pathname,
    dispatch,
    navigate,
  ]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const canUserAccessApp = () => {
    if (sessionIsValid && sessionIsValid !== 'unknown') {
      if (isSystemAdmin) {
        return true;
      } else if (userHasGroup) {
        return true;
      }
    }
    return false;
  };

  if (
    !currentUserLoading &&
    !currentGroupLoading &&
    process.env.BUGSNAG_API_KEY
  ) {
    // Initate the bugsnag instance
    Bugsnag.start({
      apiKey: process.env.BUGSNAG_API_KEY,
      plugins: [new BugsnagPluginReact()],
      onError: event => {
        // Set extra data for any errors that we report
        if (currentUser) {
          event.setUser(
            currentUser.id?.toString(),
            currentUser.primaryEmail,
            `${currentUser.firstName} ${currentUser.lastName}`
          );
        }
        if (currentGroup) {
          event.addMetadata('group', {
            id: currentGroup.id?.toString(),
            name: currentGroup.name,
            displayName: currentGroup.displayName,
          });
        }
      },
    });
  }

  return (
    <div className={classes.app} id='appContainer'>
      <NavigationHandler />
      <Routes>
        <Route path='/login' element={<Login />} />
        <Route path='/signup' element={<Signup />} />
        <Route path='/oidc/redirect' element={<OpenIDConnect />} />
        <Route path='/forgot-password/:token' element={<ForgotPassword />} />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        <Route path='/auth/confirm/:token' element={<EmailVerification />} />
        <Route path='/emails/confirm/:token' element={<EmailVerification />} />
        <Route
          key='view'
          path={`/forms/:formId/view`}
          element={<ReferralForm />}
        />

        {sessionIsValid && (
          <>
            <Route
              path='/select-chapter'
              element={<SelectChapter />}
              key='/select-chapter'
            />
            <Route
              path='/requests/pending'
              element={<WaitingRoom />}
              key='/requests/pending'
            />
          </>
        )}

        {sessionIsValid === 'unknown' ? (
          <Route path='*' element={<LoadingScreen />} />
        ) : canUserAccessApp() ? (
          <>
            <Route
              key='edit'
              path={`/forms/:formId/edit`}
              element={<ReferralForm />}
            />
            <Route key='app' path='/*' element={<ApplicationLayout />} />
          </>
        ) : (
          <Route path='*' element={<Navigate to='login' replace />} />
        )}
      </Routes>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  app: {
    height: '100vh',
    display: 'flex',
    margin: 0,
    padding: 0,

    // Hides scrollbar for entire site
    '& *': {
      scrollbarWidth: 'none', // Firefox
      msOverflowStyle: 'none', // IE and Edge
      '&::-webkit-scrollbar': {
        // Chrome and Safari
        display: 'none',
      },
    },
  },
}));

export default App;
